import React, { useEffect, useRef, useState } from 'react';
import { CatalogueOption, CatalogueRef } from '../../api/emailCatalogue';
import { getAuthoringCatalogue } from '../../api/cafWorkflows';
import { sameRef } from './reviewProofs';

/** Selections are explicit. Pagination never substitutes a different revision. */
export default function CatalogueSelect({ label, family, value, options = [], nextCursor, disabled = false, onChange }: {
  label: string; family: string; value?: CatalogueRef; options?: CatalogueOption[]; nextCursor?: string | null;
  disabled?: boolean; onChange: (ref: CatalogueRef) => void;
}) {
  const [extra, setExtra] = useState<CatalogueOption[]>([]);
  const [cursor, setCursor] = useState(nextCursor);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const generation = useRef(0);
  const seed = JSON.stringify(options.map(item => item.ref));
  useEffect(() => {
    generation.current += 1; setExtra([]); setCursor(nextCursor); setBusy(false); setProblem('');
    return () => { generation.current += 1; };
  }, [family, seed, nextCursor]);
  const items = [...options, ...extra].filter(item => item.config.family === family);
  const selected = items.find(item => sameRef(item.ref, value));
  const loadMore = async () => {
    if (!cursor || busy) return;
    const owner = generation.current;
    setBusy(true); setProblem('');
    try {
      const page = await getAuthoringCatalogue({ cursor, limit: 100 });
      if (owner !== generation.current) return;
      if (page.catalogue_blockers?.length) throw new Error('Catalogue unavailable');
      setExtra(current => [...current, ...(page.catalogue_options || [])]); setCursor(page.catalogue_next_cursor);
    } catch { if (owner === generation.current) setProblem('Could not load more options. Your selection is unchanged.'); }
    finally { if (owner === generation.current) setBusy(false); }
  };
  return <div className="cur-stack"><label className="cur-field">{label}<select className="cur-input" aria-label={label}
    disabled={disabled} value={value?.option_id ? selected ? value.option_id : 'saved:' + value.option_id : ''} onChange={event => {
      const chosen = items.find(item => item.ref.option_id === event.target.value && item.status === 'ready');
      if (chosen) onChange(chosen.ref);
    }}>
    <option value="">Choose reviewed email settings</option>
    {value?.option_id && !selected && <option value={'saved:' + value.option_id} disabled>{value.option_id} · saved revision {value.revision} (review required)</option>}
    {items.map(item => <option key={item.ref.option_id}
      value={item.ref.option_id} disabled={item.status !== 'ready'}>{item.config.name} · revision {item.ref.revision}{item.status !== 'ready' ? ` (${item.status.replace(/_/g, ' ')})` : ''}</option>)}
  </select></label>
    {selected && <p>Subject: {selected.config.subject}</p>}
    {cursor && <button type="button" disabled={busy || disabled} onClick={() => { void loadMore(); }}>{busy ? 'Loading…' : 'Load more email options'}</button>}
    {problem && <p role="alert">{problem}</p>}
  </div>;
}
